// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { parse, stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import PPWRComponent from 'Components/Service/PPWR';
import { API_PPWR_DOCUMENTS } from 'Constants';
import useAnalytics from 'Hooks/useAnalytics';
import useAsyncError from 'Hooks/useAsyncError';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useToastNotifications from 'Hooks/useToastNotifications';
import useUser from 'Hooks/useUser';
import download from 'Utils/download';

const defaultSortId = 'id-asc';
const sortFields = [
  'id',
  'code',
  'title',
  'manufacturer_name',
  'document_created_at',
  'document_updated_at',
];
const allowedSortIds = sortFields.flatMap(field => [
  `${ field }-asc`,
  `${ field }-desc`,
]);
const defaultFilters = {
  code: '',
  title: '',
  manufacturer_name: '',
  has_ppwr_document: '',
};

const getSanitizedQuery = query => {
  const sanitizedQuery = {};

  Object.entries({ code: 40, title: 300, manufacturer_name: 255 }).forEach(([key, limit]) => {
    if (typeof query?.[key] === 'string' && query[key].trim()) {
      sanitizedQuery[key] = query[key].trim().slice(0, limit);
    }
  });

  if (['0', '1'].includes(query?.has_ppwr_document)) {
    sanitizedQuery.has_ppwr_document = query.has_ppwr_document;
  }

  const sortId = `${ query?.sort }-${ query?.order }`;
  if (allowedSortIds.includes(sortId)) {
    sanitizedQuery.sort = query.sort;
    sanitizedQuery.order = query.order;
  }

  const page = Number(query?.page);
  if (Number.isInteger(page) && page > 1) {
    sanitizedQuery.page = page;
  }

  return sanitizedQuery;
};

const getProductEndpoint = (product, action) => (
  /^[a-f0-9]{32}$/.test(product?.productId)
    ? `/api/packaging/products/${ product.productId }/${ action }`
    : null
);

const getDocumentEndpoint = product => {
  const actions = {
    technical_documentation: 'ppwr-document',
    self_declaration: 'self-declaration',
  };

  return actions[product.ppwrDocumentStatus]
    ? getProductEndpoint(product, actions[product.ppwrDocumentStatus])
    : null;
};

const getErrorMessage = (error, fallback) => {
  const messages = error?.info?.errors
    ?.map(item => item.message_template || item.messageTemplate || item.message)
    .filter(Boolean);

  return messages?.join(' ') || error?.info?.message || fallback;
};

const PPWR = memo(() => {
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const throwError = useAsyncError();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { location, push, query } = useRouter();
  const sanitizedQuery = getSanitizedQuery(query);
  const queryString = stringify(sanitizedQuery);
  const currentSortId = sanitizedQuery.sort
    ? `${ sanitizedQuery.sort }-${ sanitizedQuery.order }`
    : defaultSortId;
  const sendingRef = useRef(false);

  const {
    formState: { errors: filterErrors },
    handleSubmit: submitFilters,
    register: registerFilter,
    reset: resetFilters,
    setValue: setFilterValue,
    watch: watchFilters,
  } = useForm({
    defaultValues: defaultFilters,
    mode: 'onChange',
  });
  const {
    formState: { errors: requestErrors },
    getValues: getRequestValues,
    register: registerRequest,
    reset: resetRequest,
    trigger: triggerRequest,
  } = useForm({
    defaultValues: { notes: '' },
    mode: 'onChange',
  });
  const filters = watchFilters();
  const maxLength = value => ({
    value,
    message: t('ppwr.maxLength', { maxLength: value }),
  });
  const validators = {
    code: {
      maxLength: maxLength(40),
    },
    title: {
      maxLength: maxLength(300),
    },
    manufacturer_name: {
      maxLength: maxLength(255),
    },
    notes: {
      maxLength: maxLength(1000),
    },
  };

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [requestPending, setRequestPending] = useState(false);
  const [requestedIds, setRequestedIds] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const getDocuments = useCallback(() => {
    setLoading(true);

    const path = queryString
      ? `${ API_PPWR_DOCUMENTS }?${ queryString }`
      : API_PPWR_DOCUMENTS;

    fetcher(path).then(data => {
      if (!data || !Array.isArray(data.products)) {
        throw new Error('Invalid PPWR list response');
      }

      setProducts(data.products.map(product => ({
        ...product,
        id: product.productId,
      })));
      setPagination({
        currentPage: data.currentPage,
        recordsPerPage: data.recordsPerPage,
        totalPages: data.totalPages,
        totalRecords: data.totalRecords,
      });
    }).catch(fetchError => throwError(fetchError))
      .finally(() => setLoading(false));
  }, [fetcher, queryString, throwError]);

  useEffect(() => {
    const currentQuery = parse(queryString);

    resetFilters({
      ...defaultFilters,
      code: currentQuery.code || '',
      title: currentQuery.title || '',
      manufacturer_name: currentQuery.manufacturer_name || '',
      has_ppwr_document: currentQuery.has_ppwr_document || '',
    });
  }, [queryString, resetFilters]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments, retry]);

  const handleApplyFilters = filterValues => {
    const appliedFilters = getSanitizedQuery({
      ...sanitizedQuery,
      ...filterValues,
    });
    delete appliedFilters.page;
    const filterNames = Object.entries(appliedFilters)
      .filter(([key, value]) => !['page', 'sort', 'order'].includes(key) && value)
      .map(([key]) => key)
      .join(', ');

    analytics.track('filter_ppwr_documents', { filters: filterNames });

    push({
      pathname: location.pathname,
      search: stringify(appliedFilters),
    });
  };

  const handleClearFilters = () => {
    analytics.track('clear_ppwr_filters');
    resetFilters(defaultFilters);
    push({
      pathname: location.pathname,
      search: '',
    });
  };

  const handleSort = sortId => {
    const [sort, order] = sortId.split('-');
    const nextQuery = { ...sanitizedQuery };

    delete nextQuery.page;

    if (order === 'none' || sortId === defaultSortId) {
      delete nextQuery.sort;
      delete nextQuery.order;
    } else {
      nextQuery.sort = sort;
      nextQuery.order = order;
    }

    analytics.track('sort_ppwr_documents', {
      sort: nextQuery.sort || defaultSortId.split('-')[0],
      order: nextQuery.order || defaultSortId.split('-')[1],
    });

    push({
      pathname: location.pathname,
      search: stringify(nextQuery),
    });
  };

  const handleOpenRequest = product => {
    analytics.track('open_ppwr_document_request', {
      source: 'product',
      product_code: product.code || '',
      document_status: product.ppwrDocumentStatus,
    });
    setSelectedProduct(product);
    resetRequest({ notes: '' });
  };

  const handleCloseRequest = () => {
    if (!sendingRef.current) {
      analytics.track('cancel_ppwr_document_request', {
        product_code: selectedProduct?.code || '',
      });
      setSelectedProduct(null);
      resetRequest({ notes: '' });
    }
  };

  const handleRequest = async () => {
    const endpoint = getProductEndpoint(selectedProduct, 'ppwr-document-request');

    if (!endpoint || sendingRef.current) {
      return;
    }

    const requestIsValid = await triggerRequest('notes');
    if (!requestIsValid) {
      return;
    }

    const notes = getRequestValues('notes').trim();

    sendingRef.current = true;
    setRequestPending(true);
    analytics.track('submit_ppwr_document_request', {
      product_code: selectedProduct.code,
      document_status: selectedProduct.ppwrDocumentStatus,
    });

    try {
      const response = await fetcher(endpoint, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      });
      const outcomeResponse = response?.data || response;
      let outcome = 'recorded';

      if (outcomeResponse?.alreadyRequested) {
        outcome = 'alreadyRequested';
      } else if (outcomeResponse?.notified) {
        outcome = 'sent';
      }

      analytics.track('ppwr_document_request_success', {
        product_code: selectedProduct.code,
        outcome,
      });

      setNotice(t(`ppwr.${ outcome }`));
      setRequestedIds(currentIds => [...currentIds, selectedProduct.productId]);
      setSelectedProduct(null);
      resetRequest({ notes: '' });
      setRetry(currentRetry => currentRetry + 1);
    } catch (requestFailure) {
      const fallbackKey = requestFailure.status === 404
        ? 'requestUnavailable'
        : 'requestError';

      analytics.track('ppwr_document_request_error', {
        product_code: selectedProduct.code,
        status: requestFailure.status,
        error_code: requestFailure.info?.errors?.[0]?.code,
      });

      toast.addNotification({
        type: 'error',
        title: getErrorMessage(requestFailure, t(`ppwr.${ fallbackKey }`)),
      });
    } finally {
      sendingRef.current = false;
      setRequestPending(false);
    }
  };

  const handleDownload = async product => {
    const endpoint = getDocumentEndpoint(product);

    if (!endpoint) {
      return;
    }

    setDownloadingId(product.productId);
    analytics.track('download_ppwr_document', {
      product_code: product.code,
      document_status: product.ppwrDocumentStatus,
    });

    try {
      await download(endpoint);
      analytics.track('ppwr_document_download_success', {
        product_code: product.code,
        document_status: product.ppwrDocumentStatus,
      });
    } catch (downloadError) {
      const fallbackKey = downloadError.status === 404
        ? 'downloadUnavailable'
        : 'downloadError';

      analytics.track('ppwr_document_download_error', {
        product_code: product.code,
        document_status: product.ppwrDocumentStatus,
        status: downloadError.status,
        error_code: downloadError.info?.errors?.[0]?.code,
      });

      setNotice('');
      toast.addNotification({
        type: 'error',
        title: getErrorMessage(downloadError, t(`ppwr.${ fallbackKey }`)),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <PPWRComponent
      products={ products }
      pagination={ pagination }
      filters={ filters }
      loading={ loading }
      notice={ notice }
      requester={ user }
      selectedProduct={ selectedProduct }
      requestPending={ requestPending }
      requestErrors={ requestErrors }
      requestedIds={ requestedIds }
      downloadingId={ downloadingId }
      currentSortId={ currentSortId }
      filterErrors={ filterErrors }
      registerFilter={ registerFilter }
      registerRequest={ registerRequest }
      validators={ validators }
      onFilterChange={ setFilterValue }
      onApplyFilters={ submitFilters(handleApplyFilters) }
      onClearFilters={ handleClearFilters }
      onSort={ handleSort }
      onDismissNotice={ () => setNotice('') }
      onOpenRequest={ handleOpenRequest }
      onCloseRequest={ handleCloseRequest }
      onSubmitRequest={ handleRequest }
      onDownload={ handleDownload }
    />
  );
});

PPWR.displayName = 'PPWRContainer';

export default PPWR;
